import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getReferral, getReferralBrand, setReferralBrand, clearReferral } from "@/lib/referral";

const DISMISS_KEY = "solis_ref_banner_off";

export default function ReferralBanner() {
  const { user } = useAuth();
  const [brand, setBrand] = useState("");
  const [visible, setVisible] = useState(false);
  const code = getReferral();

  useEffect(() => {
    if (!code || sessionStorage.getItem(DISMISS_KEY)) return;
    const cached = getReferralBrand();
    if (cached) {
      setBrand(cached);
      setVisible(true);
      return;
    }
    api.get(`/affiliate/brand/${code}`)
      .then((r) => {
        if (r.data?.found) {
          setReferralBrand(r.data.brand);
          setBrand(r.data.brand);
          setVisible(true);
        } else {
          clearReferral(); // stale code pointing at no house
        }
      })
      .catch(() => {});
  }, [code]);

  // attribution settled (or this signed-in user can never be attributed) — the
  // invitation did its job; the banner is for guests and fresh members only
  const settled = user && (user.referred_by || user.plan || !["model", "employer"].includes(user.role));

  return (
    <AnimatePresence>
      {visible && !settled && (
      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -12 }}
        transition={{ duration: 0.5 }}
        className="border-b border-accent/30 bg-accent/[0.06]"
        data-testid="referral-banner"
      >
        <div className="px-6 sm:px-10 py-3 flex items-center justify-center gap-3 flex-wrap text-center">
          <Sparkles size={13} className="text-accent shrink-0" />
          <p className="font-grotesk text-xs uppercase tracking-[0.18em] text-white/80">
            Invited by <span className="text-accent font-semibold" data-testid="referral-banner-brand">{brand}</span>
            <span className="hidden sm:inline text-white/45"> — your invitation attaches itself when you join</span>
          </p>
          {!user && (
            <Link to="/auth?mode=register" data-testid="referral-banner-join"
              className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-accent border border-accent/40 px-3 py-1.5 hover:bg-accent hover:text-black transition-colors">
              Join the board
            </Link>
          )}
          <button
            data-testid="referral-banner-dismiss"
            aria-label="Dismiss invitation banner"
            onClick={() => { sessionStorage.setItem(DISMISS_KEY, "1"); setVisible(false); }}
            className="text-white/40 hover:text-white transition-colors absolute right-4 sm:right-8"
          >
            <X size={14} />
          </button>
        </div>
      </motion.div>
      )}
    </AnimatePresence>
  );
}
